import { useAtomValue, useSetAtom } from "jotai";
import { useMemo, type ReactNode } from "react";
import styled from "styled-components";

import { PlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import { useUser } from "src/atoms/user";
import {
  labelOptionAtom,
  planogramHoveredContainerAtom,
  planogramHoveredItemAtom,
  planogramHoveredPegboardPlacementAtom,
  planogramHoveredShelfAtom,
  planogramImagesModeAtom,
  planogramKonvaRendererAtom,
} from "src/components/PlanogramExtensions/store/atoms";
import {
  createPlanogramAssetProvider,
  usePlanogramRendererLabels,
} from "../../common/konvaRendererAdapter";
import {
  KonvaPlanogram,
  type OverlayChannelInput,
  type PlanogramEntityRef,
  type PlanogramOverlayBadgeRequest,
  type PlanogramProductLabel,
} from "../KonvaPlanogram";
import { konvaViewerPolicy } from "./konvaViewerPolicy";

const ViewerRoot = styled.div`
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
`;

interface Props {
  onOverlayBadgeActivate?: (request: PlanogramOverlayBadgeRequest) => void;
  overlayBadges?: ReadonlyMap<string, string>;
  overlayChannels?: readonly OverlayChannelInput[];
  pog: Pog;
  scaleX: number;
  scaleY: number;
  scrollToEntityId?: string | null;
}

export const KonvaViewerPlanogram = ({
  onOverlayBadgeActivate,
  overlayBadges,
  overlayChannels,
  pog,
  scaleX,
  scaleY,
  scrollToEntityId,
}: Props) => {
  const { user } = useUser();
  const imagesMode = useAtomValue(planogramImagesModeAtom);
  const labelOption: PlanogramProductLabel = useAtomValue(labelOptionAtom);
  const setHoveredItem = useSetAtom(planogramHoveredItemAtom);
  const setHoveredPegboardPlacement = useSetAtom(planogramHoveredPegboardPlacementAtom);
  const setHoveredShelf = useSetAtom(planogramHoveredShelfAtom);
  const setHoveredContainer = useSetAtom(planogramHoveredContainerAtom);
  const labels = usePlanogramRendererLabels();

  const baseScale = Math.max(0.01, Math.min(scaleX, scaleY));
  const merchantId = user?.merchantId ?? null;
  const assetProvider = useMemo(
    () => (imagesMode ? createPlanogramAssetProvider(merchantId) : undefined),
    [imagesMode, merchantId],
  );

  const clearHover = () => {
    setHoveredItem(null);
    setHoveredPegboardPlacement(null);
    setHoveredShelf(null);
    setHoveredContainer(null);
  };

  const handleHoverChange = (entity: PlanogramEntityRef | null) => {
    if (entity?.kind === "product") {
      const item = pog.getItem(entity.id);
      clearHover();
      setHoveredItem(item instanceof PlanogramItem ? item : null);
      return;
    }

    if (entity?.kind === "placement") {
      clearHover();
      setHoveredPegboardPlacement(pog.planogram.getPegboardPlacement(entity.id) ?? null);
      return;
    }

    if (entity?.kind === "shelf") {
      clearHover();
      setHoveredShelf(pog.getShelf(entity.id) ?? null);
      return;
    }

    if (entity?.kind === "container") {
      clearHover();
      setHoveredContainer(
        pog.containers.find((candidate) => candidate.uniqueId === entity.id) ?? null,
      );
      return;
    }

    clearHover();
  };

  return (
    <ViewerRoot data-testid="konva-viewer-planogram">
      <KonvaPlanogram
        assetProvider={assetProvider}
        labels={labels}
        onHoverChange={handleHoverChange}
        onOverlayBadgeActivate={onOverlayBadgeActivate}
        overlayBadges={overlayBadges}
        overlayChannels={overlayChannels}
        policy={konvaViewerPolicy}
        pog={pog}
        scrollToEntityId={scrollToEntityId}
        viewportConfig={{
          bounds: { maxScale: baseScale * 3, minScale: baseScale * 0.5 },
          defaultState: { scale: baseScale },
        }}
        view={{
          productDisplayMode: imagesMode ? "image" : "label",
          productLabel: labelOption,
          scale: baseScale,
        }}
      />
    </ViewerRoot>
  );
};

interface GateProps extends Props {
  children: ReactNode;
}

export const KonvaViewerPlanogramGate = ({ children, ...props }: GateProps) => {
  const konvaRenderer = useAtomValue(planogramKonvaRendererAtom);

  return konvaRenderer ? <KonvaViewerPlanogram {...props} /> : <>{children}</>;
};
